import AppTheme from "@toothfairy/shared-ui/AppTheme";
import React, { forwardRef, useEffect, useRef, useState } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { View, useWindowDimensions } from "react-native";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import { isLargeScreen } from "../../Screens/MainViewContainer";

const PRESET_KEYS = [
  "today",
  "last7Days",
  "last30Days",
  "thisMonth",
  "last3Months",
  "allTime",
];

const buildStyles = (theme) => `
  /* input lives inside .tf-period-filter (not portaled) */
  .tf-period-filter .react-datepicker-wrapper {
    display: block;
    width: 320px;
  }
  .tf-period-filter .react-datepicker__input-container {
    display: block;
    width: 100%;
  }
  .tf-period-filter .react-datepicker__close-icon {
    padding-right: 6px;
    background-color: transparent;
  }
  .tf-period-filter .react-datepicker__close-icon::after {
    background-color: ${theme?.primary};
    color: #fff;
    font-size: 15px;
    font-weight: normal;
  }

  /* calendar popper is portaled to body -> scope to .tf-period-popper */
  .tf-period-popper {
    z-index: 9999;
  }
  .tf-period-popper .react-datepicker {
    display: flex;
    flex-direction: row;
    font-family: normalFont, sans-serif;
    background-color: ${theme?.backgroundColor};
    color: ${theme?.font_color};
    border-color: ${theme?.border_color};
  }
  .tf-period-popper .react-datepicker__header {
    background-color: ${theme?.backgroundColor};
    border-bottom-color: ${theme?.border_color};
  }
  .tf-period-popper .react-datepicker__current-month,
  .tf-period-popper .react-datepicker__day-name,
  .tf-period-popper .react-datepicker__day,
  .tf-period-popper .react-datepicker__time-name {
    color: ${theme?.font_color};
  }
  .tf-period-popper .react-datepicker__day:hover,
  .tf-period-popper .react-datepicker__month-text:hover {
    background-color: ${theme?.softBackgroundColor};
  }
  .tf-period-popper .react-datepicker__day--selected,
  .tf-period-popper .react-datepicker__day--keyboard-selected {
    background-color: ${theme?.primary};
    color: #fff;
  }
  .tf-period-popper .react-datepicker__day--in-range {
    background-color: ${theme?.primary};
    color: #fff;
  }
  .tf-period-popper .react-datepicker__day--in-selecting-range {
    background-color: ${theme?.softBackgroundColor};
    color: ${theme?.font_color};
  }
  .tf-period-popper .react-datepicker__day--disabled {
    color: ${theme?.mediumGrey};
  }
  .tf-period-popper .react-datepicker__navigation--previous {
    background-color: transparent;
  }
  .tf-period-popper--range .react-datepicker__navigation--previous {
    left: 130px;
  }
  .tf-period-popper .react-datepicker__navigation--next {
    background-color: transparent;
  }
  .tf-period-popper .react-datepicker__close-icon {
    padding-right: 6px;
    background-color: transparent;
  }
  .tf-period-popper .react-datepicker__close-icon::after {
    background-color: ${theme?.primary};
    color: #fff;
    font-size: 15px;
    font-weight: normal;
  }
  .tf-period-popper .tf-period-presets {
    display: flex;
    flex-direction: column;
    border-right: 1px solid ${theme?.border_color};
    padding: 8px;
    gap: 2px;
    min-width: 118px;
    font-family: normalFont, sans-serif;
  }
  .tf-period-popper .tf-period-preset-btn {
    text-align: left;
    padding: 6px 10px;
    border-radius: 8px;
    border: none;
    background: transparent;
    cursor: pointer;
    font-size: 12px;
    font-family: normalFont, sans-serif;
    color: ${theme?.font_color};
    white-space: nowrap;
  }
  .tf-period-popper .tf-period-preset-btn:hover {
    background: ${theme?.softBackgroundColor};
  }
`;

const ToothFairyPeriodFilter = ({
  startDate,
  endDate,
  onUpdateStartDate,
  onUpdateEndDate,
  paddingBottom = 5,
  selectsSingle = false,
}) => {
  const { t } = useTranslation();
  const { currentTheme } = AppTheme.useTheme();
  const { width } = useWindowDimensions();
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [draftStart, setDraftStart] = useState(startDate);
  const [draftEnd, setDraftEnd] = useState(endDate);
  const draftStartRef = useRef(startDate);
  const draftEndRef = useRef(endDate);
  const theme = currentTheme?.theme;

  useEffect(() => {
    if (!isCalendarOpen) {
      setDraftStart(startDate);
      setDraftEnd(endDate);
      draftStartRef.current = startDate;
      draftEndRef.current = endDate;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate, isCalendarOpen]);

  const commitChanges = () => {
    if (selectsSingle) {
      const changed = draftStartRef.current !== startDate;
      if (changed) {
        onUpdateStartDate?.(draftStartRef.current);
        onUpdateEndDate?.(draftStartRef.current);
      }
      return;
    }
    const changed =
      draftStartRef.current !== startDate || draftEndRef.current !== endDate;
    if (changed) {
      onUpdateStartDate?.(draftStartRef.current);
      onUpdateEndDate?.(draftEndRef.current);
    }
  };

  const handleClose = () => {
    setIsCalendarOpen(false);
    commitChanges();
  };

  const CustomInput = forwardRef((props, ref) => (
    <input
      {...props}
      ref={ref}
      readOnly
      style={{
        width: "210px",
        maxWidth: "210px",
        padding: "9px 36px 9px 12px",
        backgroundColor: theme?.inputBackgroundColor,
        color: theme?.font_color,
        border: `1px solid ${theme?.border_color}`,
        borderRadius: 8,
        fontFamily: "normalFont, sans-serif",
        fontSize: 13,
        cursor: "pointer",
        outline: "none",
        boxSizing: "border-box",
        height: "40px",
      }}
    />
  ));

  const handleChange = (update) => {
    if (selectsSingle) {
      draftStartRef.current = update;
      setDraftStart(update);
      return;
    }
    const [start, end] = update || [null, null];
    setDraftStart(start);
    setDraftEnd(end);
    draftStartRef.current = start;
    draftEndRef.current = end;
  };

  const applyPreset = (key) => {
    if (key === "allTime") {
      draftStartRef.current = null;
      draftEndRef.current = null;
      setDraftStart(null);
      setDraftEnd(null);
      onUpdateStartDate?.(null);
      onUpdateEndDate?.(null);
      setIsCalendarOpen(false);
      return;
    }
    const end = dayjs().endOf("day").toDate();
    let start;
    switch (key) {
      case "today":
        start = dayjs().startOf("day").toDate();
        break;
      case "last7Days":
        start = dayjs().subtract(6, "days").startOf("day").toDate();
        break;
      case "last30Days":
        start = dayjs().subtract(29, "days").startOf("day").toDate();
        break;
      case "thisMonth":
        start = dayjs().startOf("month").toDate();
        break;
      case "last3Months":
        start = dayjs().subtract(3, "months").startOf("month").toDate();
        break;
      default:
        return;
    }
    draftStartRef.current = start;
    draftEndRef.current = end;
    setDraftStart(start);
    setDraftEnd(end);
    onUpdateStartDate?.(start);
    onUpdateEndDate?.(end);
    setIsCalendarOpen(false);
  };

  const CalendarContainer = ({ className, children }) => (
    <div
      className={className}
      style={{
        backgroundColor: theme?.backgroundColor,
        color: theme?.font_color,
        borderColor: theme?.border_color,
      }}
    >
      {!selectsSingle && (
        <div className="tf-period-presets">
          {PRESET_KEYS.map((key) => (
            <button
              key={key}
              type="button"
              className="tf-period-preset-btn"
              onClick={() => applyPreset(key)}
            >
              {t(`common.periodPresets.${key}`, { defaultValue: key })}
            </button>
          ))}
        </div>
      )}
      <div>{children}</div>
    </div>
  );

  return (
    <>
      <style>{buildStyles(theme)}</style>
      <View
        className="tf-period-filter"
        style={{
          flexDirection: "row",
          alignItems: "center",
          paddingBottom,
          marginLeft: 5,
          zIndex: 1,
          maxWidth: !isLargeScreen() && width - 30,
        }}
      >
        <DatePicker
          customInput={<CustomInput />}
          selectsRange={!selectsSingle}
          selected={selectsSingle ? draftStart : undefined}
          startDate={selectsSingle ? undefined : draftStart}
          endDate={selectsSingle ? undefined : draftEnd}
          onChange={handleChange}
          dateFormat="dd/MM/yyyy"
          isClearable
          placeholderText={
            selectsSingle
              ? t("common.selectDate", { defaultValue: "Select date" })
              : t("common.selectPeriod", {
                  defaultValue: "Select period",
                })
          }
          maxDate={new Date()}
          open={isCalendarOpen}
          onInputClick={() => setIsCalendarOpen(true)}
          onClickOutside={handleClose}
          onCalendarOpen={() => setIsCalendarOpen(true)}
          onCalendarClose={handleClose}
          calendarContainer={CalendarContainer}
          popperClassName={
            selectsSingle ? "tf-period-popper" : "tf-period-popper tf-period-popper--range"
          }
        />
      </View>
    </>
  );
};

export default ToothFairyPeriodFilter;
